import { useState, useEffect } from 'react';
import { getAdminUser, adminSetAdmin, adminUpdateSubscription, adminResetDailyLimit, adminDeleteUser, adminSetLearnerLevel } from '../../api/admin';
import s from './AdminPages.module.css';

export default function AdminUserDetail({ userId, onBack }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // tracks which action is in flight
  const [actionMsg, setActionMsg] = useState(null); // { type: 'success'|'error', text: string }
  const [extendDays, setExtendDays] = useState(7);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    setLoading(true);
    getAdminUser(userId)
      .then(setUser)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <p className={s.loadingText}>Loading user...</p>;
  if (error) return <p className={s.errorText}>Error: {error}</p>;
  if (!user) return null;

  const sub = user.subscription;

  async function runAction(key, fn) {
    setActionLoading(key);
    setActionMsg(null);
    try {
      const result = await fn();
      setActionMsg({ type: 'success', text: result?.message || 'Done.' });
      // Refresh user data
      const updated = await getAdminUser(userId);
      setUser(updated);
    } catch (err) {
      setActionMsg({ type: 'error', text: err.message || 'Action failed.' });
    } finally {
      setActionLoading(null);
    }
  }

  function handleSetAdmin(isAdmin) {
    runAction('set-admin', async () => {
      await adminSetAdmin(userId, isAdmin);
      return { message: `Admin access ${isAdmin ? 'granted' : 'removed'}.` };
    });
  }

  function handleComp() {
    runAction('comp', async () => {
      await adminUpdateSubscription(userId, 'comp');
      return { message: 'Account comped — full access granted.' };
    });
  }

  function handleExtendTrial() {
    runAction('extend-trial', async () => {
      await adminUpdateSubscription(userId, 'extend_trial', { days: extendDays });
      return { message: `Trial extended by ${extendDays} day(s).` };
    });
  }

  function handleCancel() {
    runAction('cancel', async () => {
      await adminUpdateSubscription(userId, 'cancel');
      return { message: 'Subscription cancelled.' };
    });
  }

  function handleRevoke() {
    runAction('revoke', async () => {
      await adminUpdateSubscription(userId, 'revoke');
      return { message: 'Access revoked.' };
    });
  }

  function handleResetLimit() {
    runAction('reset-limit', async () => {
      const result = await adminResetDailyLimit(userId);
      return { message: `Daily story limit reset for ${result.reset_count} learner(s).` };
    });
  }

  async function handleDeleteUser() {
    setActionLoading('delete');
    setActionMsg(null);
    try {
      const result = await adminDeleteUser(userId);
      setActionMsg({ type: 'success', text: result?.message || 'User deleted.' });
      setTimeout(() => onBack(), 1200);
    } catch (err) {
      setActionMsg({ type: 'error', text: err.message || 'Action failed.' });
    } finally {
      setActionLoading(null);
    }
  }

  const isActing = actionLoading !== null;

  return (
    <div>
      <button onClick={onBack} className={s.backBtn}>
        &larr; Back to Users
      </button>

      <h1 className={s.title}>{user.name || user.email}</h1>

      {/* ── Action feedback ── */}
      {actionMsg && (
        <div className={actionMsg.type === 'success' ? s.successBanner : s.errorBanner}>
          {actionMsg.text}
        </div>
      )}

      {/* ── Admin Actions ── */}
      <div className={s.card}>
        <h2 className={s.cardTitle}>Admin Actions</h2>

        <div className={s.actionsGrid}>
          {/* Admin toggle */}
          <div className={s.actionBlock}>
            <p className={s.actionLabel}>Admin Access</p>
            <p className={s.actionDesc}>Currently: <strong>{user.is_admin ? 'Admin' : 'Regular user'}</strong></p>
            {user.is_admin ? (
              <button
                className={s.actionBtnDanger}
                onClick={() => handleSetAdmin(false)}
                disabled={isActing}
              >
                {actionLoading === 'set-admin' ? 'Saving…' : 'Remove Admin'}
              </button>
            ) : (
              <button
                className={s.actionBtn}
                onClick={() => handleSetAdmin(true)}
                disabled={isActing}
              >
                {actionLoading === 'set-admin' ? 'Saving…' : 'Grant Admin'}
              </button>
            )}
          </div>

          {/* Comp account */}
          <div className={s.actionBlock}>
            <p className={s.actionLabel}>Comp Account</p>
            <p className={s.actionDesc}>Grant full access indefinitely, no payment required.</p>
            <button
              className={s.actionBtn}
              onClick={handleComp}
              disabled={isActing}
            >
              {actionLoading === 'comp' ? 'Saving…' : 'Comp Account'}
            </button>
          </div>

          {/* Extend trial */}
          <div className={s.actionBlock}>
            <p className={s.actionLabel}>Extend Trial</p>
            <p className={s.actionDesc}>Add days to trial. Current end: {sub?.trial_ends_at ? formatDate(sub.trial_ends_at) : 'N/A'}</p>
            <div className={s.actionRow}>
              <input
                type="number"
                min={1}
                max={365}
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
                className={s.daysInput}
              />
              <span className={s.daysLabel}>days</span>
              <button
                className={s.actionBtn}
                onClick={handleExtendTrial}
                disabled={isActing}
              >
                {actionLoading === 'extend-trial' ? 'Saving…' : 'Extend'}
              </button>
            </div>
          </div>

          {/* Reset daily story limit */}
          <div className={s.actionBlock}>
            <p className={s.actionLabel}>Reset Daily Story Limit</p>
            <p className={s.actionDesc}>Clear today's usage so the user can generate another story today.</p>
            <button
              className={s.actionBtn}
              onClick={handleResetLimit}
              disabled={isActing}
            >
              {actionLoading === 'reset-limit' ? 'Resetting…' : 'Reset Limit'}
            </button>
          </div>

          {/* Cancel subscription */}
          <div className={s.actionBlock}>
            <p className={s.actionLabel}>Cancel Subscription</p>
            <p className={s.actionDesc}>Mark subscription as cancelled. Does not delete data.</p>
            <button
              className={s.actionBtnWarning}
              onClick={handleCancel}
              disabled={isActing}
            >
              {actionLoading === 'cancel' ? 'Saving…' : 'Cancel Subscription'}
            </button>
          </div>

          {/* Revoke access */}
          <div className={s.actionBlock}>
            <p className={s.actionLabel}>Revoke Access</p>
            <p className={s.actionDesc}>Immediately block access. User will see the subscription page.</p>
            <button
              className={s.actionBtnDanger}
              onClick={handleRevoke}
              disabled={isActing}
            >
              {actionLoading === 'revoke' ? 'Saving…' : 'Revoke Access'}
            </button>
          </div>
        </div>

        {/* Delete user — separate dangerous zone */}
        <div className={s.dangerZone}>
          <p className={s.dangerZoneTitle}>Danger Zone</p>
          {!showDeleteConfirm ? (
            <button
              className={s.actionBtnDanger}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isActing}
            >
              Delete User
            </button>
          ) : (
            <div className={s.deleteConfirm}>
              <p className={s.deleteConfirmText}>
                This permanently deletes <strong>{user.email}</strong> and all their data. This cannot be undone.
              </p>
              <div className={s.deleteConfirmBtns}>
                <button
                  className={s.actionBtnDanger}
                  onClick={handleDeleteUser}
                  disabled={isActing}
                >
                  {actionLoading === 'delete' ? 'Deleting…' : 'Yes, Delete Permanently'}
                </button>
                <button
                  className={s.actionBtn}
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isActing}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Account info (existing, unchanged) ── */}
      <div className={s.card}>
        <h2 className={s.cardTitle}>Account</h2>
        <div className={s.fieldGrid}>
          <Field label="Email" value={user.email} />
          <Field label="Name" value={user.name || '-'} />
          <Field label="Admin" value={user.is_admin ? 'Yes' : 'No'} />
          <Field label="Timezone" value={user.timezone || '-'} />
          <Field label="Locale" value={user.locale || '-'} />
          <Field label="Created" value={formatDate(user.created_at)} />
          <Field label="Total Stories" value={user.total_stories} />
        </div>
      </div>

      {/* ── Subscription (existing, unchanged) ── */}
      <div className={s.card}>
        <h2 className={s.cardTitle}>Subscription</h2>
        {sub ? (
          <div className={s.fieldGrid}>
            <Field label="Plan" value={sub.plan_type} />
            <Field label="Status" value={sub.subscription_status} />
            <Field label="Trial Ends" value={sub.trial_ends_at ? formatDate(sub.trial_ends_at) : '-'} />
            <Field label="Stripe ID" value={sub.stripe_subscription_id || '-'} />
            <Field label="Period Start" value={formatDate(sub.current_period_start)} />
            <Field label="Period End" value={formatDate(sub.current_period_end)} />
          </div>
        ) : (
          <p className={s.emptyText}>No subscription found</p>
        )}
      </div>

      {/* ── Learners (existing, unchanged) ── */}
      <div className={s.card}>
        <h2 className={s.cardTitle}>Learners ({user.learners?.length || 0})</h2>
        {user.learners?.length > 0 ? (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>Name</th>
                <th className={s.th}>Age</th>
                <th className={s.th}>Pinyin</th>
                <th className={s.th}>Level</th>
                <th className={s.th}>Stories</th>
                <th className={s.th}>Created</th>
                <th className={s.th}>Debug: Set Level</th>
              </tr>
            </thead>
            <tbody>
              {user.learners.map((l) => (
                <tr key={l.id}>
                  <td className={s.td}>{l.name}</td>
                  <td className={s.td}>{l.age || '-'}</td>
                  <td className={s.td}>{l.pinyin_show ? 'On' : 'Off'}</td>
                  <td className={s.td}>{l.cached_level ?? 0}</td>
                  <td className={s.td}>{l.story_count}</td>
                  <td className={s.td}>{formatDate(l.created_at)}</td>
                  <td className={s.td}>
                    <LearnerLevelControl
                      learnerId={l.id}
                      currentLevel={l.cached_level ?? 0}
                      onSuccess={async () => {
                        const updated = await getAdminUser(userId);
                        setUser(updated);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className={s.emptyText}>No learners</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <span className={s.fieldLabel}>{label}</span>
      <span className={s.fieldValue}>{value}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function LearnerLevelControl({ learnerId, currentLevel, onSuccess }) {
  const [value, setValue] = useState(Math.max(currentLevel || 0, 1));
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'ok'|'err', text }

  async function handleSet() {
    const lvl = parseInt(value, 10);
    if (!Number.isInteger(lvl) || lvl < 1 || lvl > 42) {
      setStatus({ type: 'err', text: 'Level must be 1–42' });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const result = await adminSetLearnerLevel(learnerId, lvl);
      setStatus({ type: 'ok', text: `✓ Level set to ${result.new_level}` });
      await onSuccess();
    } catch (err) {
      setStatus({ type: 'err', text: err.message || 'Failed' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input
        type="number"
        min={1}
        max={42}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={busy}
        style={{ width: 60 }}
      />
      <button onClick={handleSet} disabled={busy}>
        {busy ? 'Setting…' : 'Set Level'}
      </button>
      {status && (
        <span style={{ color: status.type === 'ok' ? '#0a7d3a' : '#b00020', fontSize: 13 }}>
          {status.text}
        </span>
      )}
    </div>
  );
}
